import React, {
  memo, useRef, useState, useContext,
  useEffect,
} from 'react';
import {
  Button,
  Col,
  Dropdown,
  Menu,
  Row,
  Select,
  Space,
  Spin,
} from 'antd';
import { useQuery } from 'react-query';
import classnames from 'classnames';
import {
  useParams, useHistory,
} from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ReactGA from 'react-ga4';
import { useAuthenticator } from '@aws-amplify/ui-react';

import CustomChart from '../../../components/custom-chart';
import {
  dateRanges,
} from '../constant';
import CustomSwitch from '../../../components/custom-switch';
import {
  parameterConfigurations,
  getExternalObsDatasets,
  getExternalParameters,
  getExternalObs,
  getExternalObsLatestData,
} from '../../../services/dataset.service';
import {
  formatParameterObservation,
  getExternalParameterObservations,
} from '../../../utils';
import DetailHeader from '../components/DetailHeader';
import { brandingConfig } from '../../../config/index';
import './ParameterDetail.scss';
import WindroseChart from '../../../components/custom-chart/windroseChart';
import { OptionsIcon } from '../../../components/icons';
import MapQueryTypes from '../../../services/query-types/map';

import UserContext from '../../../contexts/UserContext';
import ParameterFilters from '../../../components/parameter/ParameterFilters';
import DatasetMessage from '../../../components/dataset-message';
import { useData } from '../../../contexts/DataContext';
import CtaBar from '../../../components/cta-bar';
import { CTA_BUTTONS } from '../../../components/cta-bar/constants';
import CustomModal from '../../../components/modal';
import SocialShareSenderModal from '../components/SocialShareSenderModal';
import { ParameterDetailsEvent } from '../../../constant/google-analytics/constant';
import CommunitySignupModal from '../components/CommunitySignupModal';
import DownloadChartModal from './DownloadChartModal';
import SocialShareRecipientModal from '../../dashboard/components/SocialShareRecipientModal';
import {
  getChartImage,
  postSharingImage,
  base64UrlSafeEncode,
  downloadChartImage,
} from '../../../utils/socialSharing';
import { featureFlag } from '../../../constant';
import FeaturedParameters from '../components/FeaturedParameters';

const { Option } = Select;

const colorPalette = brandingConfig.map.parameters;

const ExternalParameterDetail = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const history = useHistory();
  const { unitPreferences } = useContext(UserContext);
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
  const {
    useGlobalData,
  } = useData();
  const {
    extObsDatasetId: extObsDatasetIdParam,
    extParameterId: extParameterIdParam,
  } = useParams();

  const extObsDatasetId = Number(extObsDatasetIdParam);
  const extParameterId = Number(extParameterIdParam);

  const [chartData, setChartData] = useState([]);
  const [dataView, setDataView] = useState(true);
  const [dateRange, setDateRange] = useState(dateRanges[0].value);
  // const [parameterIds, setParameterIds] = useState(extParameterId);
  const [dotsVisible, setDotsVisible] = useState(false);
  const [linesVisible, setLinesVisible] = useState(true);
  // const [comparisonParameters, setComparisonParameters] = useState([]);
  const [categorySelected, setCategorySelected] = useState('');
  const [categoryArray, setCategoryArray] = useState([]);

  const [filterValue, setFilterValue] = useState({
    extObsDatasetIds: [extObsDatasetId],
    extParameterIds: extParameterId,
    range: dateRange,
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState('');
  const [shareImageLink, setShareImageLink] = useState('');
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showDownloadChartModal, setShowDownloadChartModal] = useState(false);
  const [isChartTooltipShowing, setIsChartTooltipShowing] = useState();

  const apexChartRef = useRef(null);
  const downloadChartRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    setFilterValue({
      ...filterValue,
      extParameterIds: extParameterId,
    });
  }, [extParameterId]);

  const optionsMenu = (
    <Menu>
      <Menu.Item onClick={() => setDotsVisible((prev) => !prev)}>
        {dotsVisible ? 'Turn off data points' : 'Turn on data points'}
      </Menu.Item>
      <Menu.Item onClick={() => setLinesVisible((prev) => !prev)}>
        {linesVisible ? 'Turn off lines' : 'Turn on lines'}
      </Menu.Item>
      {featureFlag.socialSharingTimeSeries && (
        <Menu.Item className="downloadChartSubMenu" onClick={() => setShowDownloadChartModal(true)}>
          Download Hi-Res Image
        </Menu.Item>
      )}
    </Menu>
  );

  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const {
    organizationsResult,
  } = useGlobalData();

  const {
    data: parameterConfigurationsResult,
    // isLoading: parameterConfigurationsIsLoading,
  } = useQuery(MapQueryTypes.REST_PARAMETER_CONFIGURATIONS, parameterConfigurations, { refetchOnWindowFocus: false });

  const {
    data: externalObsDatasets,
    // isLoading: externalObsDatasetsIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_DATASETS, getExternalObsDatasets, { refetchOnWindowFocus: false });

  const {
    data: externalParameters,
    // isLoading: externalParametersIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_PARAMETERS, getExternalParameters, { refetchOnWindowFocus: false });

  const {
    data: externalObsLatest,
    // isLoading: externalObsLatestIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_LATEST_DATA, getExternalObsLatestData, { refetchOnWindowFocus: false });

  const {
    data: externalObs,
    isLoading: externalObsIsLoading,
  } = useQuery([MapQueryTypes.REST_EXTERNAL_OBS, filterValue], () => getExternalObs(filterValue));

  const obsDataset = externalObsDatasets?.find((o) => o.ext_obs_dataset_id === extObsDatasetId);
  const organization = organizationsResult?.find((o) => o.organization_id === obsDataset?.organization_id);
  const parameter = externalParameters?.find((o) => o.ext_parameter_id === extParameterId);
  const parameterConfig = parameterConfigurationsResult?.find((o) => o.standard_name === parameter?.standard_name);
  let parameterName = parameterConfig?.display_name?.en;
  if (parameter?.datum && parameter.datum?.length > 0) {
    if (parameter.datum === 'LWD') {
      parameterName += ` above ${parameter.datum}`;
    } else {
      parameterName += ` (${parameter.datum})`;
    }
  }
  const parameterDescription = parameterConfig?.display_description?.en;
  const parameterUnit = chartData?.[0]?.data?.[0]?.y?.split(' ')?.[1];

  const isWindroseChart = (parameter?.standard_name === 'wind_from_direction');
  const windroseSvgNode = document.getElementById('seagull-windrose-chart-high-res');

  const externalParameterObs = getExternalParameterObservations(
    obsDataset,
    externalParameters,
    externalObsLatest,
    parameterConfigurationsResult,
    unitPreferences,
  );

  externalParameterObs.sort((a, b) => a.depth - b.depth);

  const datasetMessage = obsDataset?.obs_dataset_message?.message;

  const onDownloadPng = async () => {
    await downloadChartImage('png', 'seagull-share-chart-high-res', windroseSvgNode, obsDataset.org_platform_id, obsDataset.platform_name, parameterName, parameterUnit);
  };

  const onDownloadSvg = async () => {
    await downloadChartImage('svg', 'seagull-share-chart-high-res', windroseSvgNode, obsDataset.org_platform_id, obsDataset.platform_name, parameterName, parameterUnit);
  };

  const handleSetCategoryArray = (childData) => {
    setCategoryArray(childData);
  };

  useEffect(() => {
    if (externalObs && externalObs.timestamps.length > 0 && parameter && parameterConfigurationsResult && parameterConfigurationsResult.length > 0) {
      const { timestamps, parameter_obs: parameterObs } = externalObs;

      if (parameterObs.values[parameter.ext_parameter_id]) {
        const parameterData = {
          name: 'Surface',
          color: colorPalette[0],
          data: [],
        };

        parameterObs.values[parameter.ext_parameter_id].forEach((obs, index) => {
          const timestamp = timestamps[index] * 1000;

          const value = formatParameterObservation(
            parameterConfigurationsResult, parameter, { value: obs }, unitPreferences,
          );

          if (value) {
            parameterData.data.push({
              x: timestamp,
              y: value,
              children: [],
            });
          }
        });
        setChartData([parameterData]);
        setContainerHeight(containerRef.current.offsetHeight);
        setContainerWidth(containerRef.current.offsetHeight);
      }
    }
  }, [externalObs, unitPreferences, parameterConfigurationsResult, parameter]);

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setFilterValue({
      ...filterValue,
      range: newDateRange,
    });
  };

  const onDetailChange = (checked) => {
    setDataView(!checked);
  };

  const onClick = (parameter) => {
    setDataView(true);

    if (parameter.ext_parameter_id !== extParameterId) {
      history.push(`/data-console/external/${extObsDatasetId}/parameter/${parameter.ext_parameter_id}`);
    }
  };

  const breadcrumb = [
    {
      name: obsDataset?.platform_name,
      href: `/data-console/external/${extObsDatasetId}`,
    },
    {
      name: parameterName,
      href: `/data-console/external/${extObsDatasetId}/parameter/${parameter?.ext_parameter_id}`,
    },
  ];

  // const onClickCompare = (parameter) => {
  //   if (comparisonParameters.includes(parameter)) {
  //     setComparisonParameters(comparisonParameters.filter((p) => p !== parameter));
  //     setParameterIds(comparisonParameters.filter((p) => p !== parameter).map((p) => p.parameter_id).join(','));
  //   } else {
  //     setComparisonParameters([...comparisonParameters, parameter]);
  //     setParameterIds([...comparisonParameters, parameter].map((p) => p.parameter_id).join(','));
  //   }
  // };

  const onCategoriesChange = (category) => {
    setCategorySelected(category);
  };

  const chooseChartType = () => {
    if (isWindroseChart) {
      return (
        <WindroseChart
          series={chartData}
          height={isMobile ? (containerWidth - 10) : (containerHeight - 300)}
          width={containerWidth - 10}
          className="windroseStyles"
        />
      );
    }

    return (
      <CustomChart
        type={chartData[0].data.length > 1 ? 'line' : 'scatter'}
        dotsVisible={chartData[0].data.length > 1 ? dotsVisible : true}
        linesVisible={chartData[0].data.length > 1 ? linesVisible : false}
        series={chartData}
        height={containerHeight - (isMobile ? 10 : 300)}
      />
    );
  };

  const handleShareModalClick = async () => {
    const svgImage = await getChartImage('svg', 'seagull-share-chart', windroseSvgNode, obsDataset.platform_name, parameterName, parameterUnit);
    const pngImage = await getChartImage('png', 'seagull-share-chart', windroseSvgNode, obsDataset.platform_name, parameterName, parameterUnit);
    setShareImageUrl(svgImage);

    postSharingImage(svgImage, pngImage).then((objectUrl) => {
      if (objectUrl) {
        const browserUrl = new URL(window.location.href);
        const parts = objectUrl[0]?.split('/');
        const s3ObjectTokenId = parts[parts?.length - 1];
        const title = breadcrumb.map(({ name }) => name).join(' - ');
        const shareObj = { t: title, i: s3ObjectTokenId };

        browserUrl.searchParams.delete('share');
        browserUrl.searchParams.append('share', base64UrlSafeEncode(JSON.stringify(shareObj)));
        setShareImageLink(browserUrl.href);
      }
    });

    setShowShareModal(!showShareModal);
  };

  const hasUserSeenShareModal = sessionStorage.getItem('hasUserSeenShareModal');

  const chartDiv = document.getElementById('chart');

  // Checks if the chart has been touched to clear space for the tooltip on mobile
  const handleChartTooltipSpacing = () => {
    setIsChartTooltipShowing(true);
    chartDiv.removeEventListener('touchstart', handleChartTooltipSpacing);
  };

  if (chartDiv) {
    chartDiv.addEventListener('touchstart', handleChartTooltipSpacing);
  }

  const handleCommunityModalSignup = (value) => {
    setShowSignupModal(value);
  };

  // Checks to see if chart is fully loaded
  const timeSeriesChart = document.getElementById('apexchartsseagull-share-chart');
  const isChartLoaded = Boolean(windroseSvgNode) || Boolean(timeSeriesChart);

  const ctaButtons = [
    {
      key: CTA_BUTTONS.CTA_FAVORITE,
      visible: false,
      isDefault: false,
    },
    {
      key: CTA_BUTTONS.CTA_CREATE_ALERT,
      visible: false,
      isDefault: false,
    },
    {
      key: CTA_BUTTONS.CTA_DOWNLOAD,
      visible: false,
      isDefault: false,
      isDisabled: !isChartLoaded,
    },
  ];

  if (featureFlag.socialSharingTimeSeries) {
    ctaButtons.unshift({
      key: CTA_BUTTONS.CTA_SHARE,
      visible: true,
      onClick: () => {
        handleShareModalClick();
        ReactGA.event(ParameterDetailsEvent.ShareModalSelect);
      },
      isDefault: false,
      isDisabled: !isChartLoaded, // Want to prevent user from clicking buttons involving chart before chart loads
    });
  }

  return (
    <div className="console-detail">
      <DetailHeader
        breadcrumb={breadcrumb}
        dataset={obsDataset}
        isMobile={isMobile}
        organization={organization}
        selectedParameter={extParameterId}
        isPlatformsDataConsole={false}
        // statusEl={(
        //   (obsDataset && !isPlatformsDataConsole) ? (
        //     <PlatformEvent
        //       properties={obsDataset.obs_dataset_platform_assignment.platform.platform_event}
        //     />
        //   ) : (<span />)
        // )}
        // TODO: add statusEl prop to DetailHeader
        parameters={externalParameterObs}
        chartRef={downloadChartRef}
        apexChartRef={apexChartRef}
        parameter={parameter}
        isChartLoaded={isChartLoaded}
      />
      {/* {collectionStatus && (
        <CollectionStatusAlert collectionStatus={collectionStatus} />
      )} */}
      <div
        className="parameter-detail"
        style={{ padding: isMobile ? '0 20px' : '20px 0' }}
      >
        {isMobile
          ? (
            <CustomSwitch
              defaultChecked={!dataView}
              unCheckedChildren={parameterName}
              checkedChildren="Discover More"
              className="detail-switch"
              onChange={onDetailChange}
            />
          ) : ''}
        <Row wrap={false} style={{ flex: 1, overflow: 'hidden' }}>
          <Col
            hidden={isMobile && !dataView}
            lg={16}
            md={16}
            xs={24}
            flex={1}
            style={!isMobile ? {
              backgroundColor: brandingConfig.colors.primaryDark2,
              borderTopLeftRadius: 10,
              borderBottomLeftRadius: 10,
              overflowX: 'hidden',
              overflowY: 'scroll',
              padding: '0 20px',
            } : {
              overflowX: 'hidden',
              overflowY: 'scroll',
            }}
            className="parameter-detail-main"
            ref={containerRef}
          >
            {datasetMessage && (
              <DatasetMessage message={datasetMessage} />
            )}
            <div className={classnames(isMobile ? 'h2-w' : 'h1-w', 'd-flex', 'parameter-name')}>
              {parameterName}
            </div>
            <div className="parameter-description">
              {parameterDescription}
            </div>
            {/* {(parameterDepths.length > 1 || parameter?.description)
              && (
                <div className="parameter-description">
                  {parameter?.description}
                  {parameterDepths.length > 1 && (
                    <>
                      {' '}
                      Click on the depths on the right of the graph to see the data at those depths.
                    </>
                  )}
                </div>
              )} */}
            <div
              className="text-white date-range-filter"
              style={{
                paddingBottom: 15,
                display: isMobile ? 'block' : 'flex',
                alignItems: 'center',
                fontWeight: 500,
                marginBottom: isMobile && isChartTooltipShowing ? 40 : 0,
              }}
            >
              <span>Show data from last available:</span>
              {isMobile
                ? (
                  <Select
                    className="period-select"
                    defaultValue={dateRange}
                    onChange={handleDateRangeChange}
                  >
                    {dateRanges.map((period) => (
                      <Option key={period.value} value={period.value}>{period.name}</Option>
                    ))}
                  </Select>
                ) : (
                  <Menu
                    mode="horizontal"
                    className="period-menu"
                    onClick={(e) => handleDateRangeChange(e.key)}
                    selectedKeys={[dateRange]}
                  >
                    {dateRanges.map((period) => (
                      <Menu.Item key={period.value}>
                        {period.name}
                      </Menu.Item>
                    ))}
                  </Menu>
                )}
              {!isMobile && ( // Only show workspace link on desktop
                <div style={{ marginLeft: 'auto', display: 'flex' }}>
                  <CtaBar
                    buttonsConfig={ctaButtons}
                  />
                  <Dropdown
                    overlay={optionsMenu}
                    placement="bottomRight"
                  >
                    <Space>
                      <Button
                        className="options-button"
                        icon={<OptionsIcon style={{ rotate: '90deg', fontSize: 27 }} />}
                        disabled={!isChartLoaded}
                      />
                    </Space>
                  </Dropdown>
                </div>
              )}
              {isMobile && (
                <Select
                  value={null}
                  placeholder="Chart options"
                  className="period-select"
                  onChange={(val) => {
                    if (val === 'toggleDots') setDotsVisible((prev) => !prev);
                    if (val === 'toggleLines') setLinesVisible((prev) => !prev);
                  }}
                  style={{ marginTop: 8 }}
                >
                  <Option value="toggleDots">{dotsVisible ? 'Turn off data points' : 'Turn on data points'}</Option>
                  <Option value="toggleLines">{linesVisible ? 'Turn off lines' : 'Turn on lines'}</Option>
                </Select>
              )}
            </div>
            <Row className="parameter-chart">
              <Col xs={24}>
                {
                  (externalObsIsLoading)
                  && (chartData[0] === undefined
                    || chartData[0]?.data?.length === 0)
                    ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '18%',
                      }}
                      >
                        <Spin />
                      </div>
                    )
                    : (
                      <div
                        className={classnames('bg-dark', 'parameter-card')}
                        ref={chartRef}
                        style={{ border: 'none', borderRadius: 10, display: 'flex' }}
                      >
                        {(chartData[0]?.data?.length > 0 || chartData[0] === undefined) && (
                          <div style={{ width: chartData.length > 0 ? '100%' : '94%' }}>
                            {chartData.length > 0 && chartData[0] !== undefined
                              && chooseChartType()}
                            <div
                              className="custom-tooltip-container"
                              style={{
                                top: isMobile ? -60 : 0,
                              }}
                            >
                              <div
                                className="custom-tooltip-text"
                                style={{
                                  opacity: 0,
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {/* {parameterDepths.length > 1 && (
                          <div
                            className="depths-toolbar"
                            style={{ overflowY: 'scroll', height: chartHeight }}
                          >
                            <div
                              data-color="white"
                              onClick={() => onDepthClick('ALL')}
                            >
                              ALL
                              <div style={{
                                height: 3,
                                width: '40%',
                                backgroundColor: isAllDepthsSelected ? 'white' : '',
                              }}
                              />
                            </div>
                            {parameterDepths.map((depth, index) => (
                              <div
                                key={index}
                                data-color={getDepthColor(depth, index)}
                                onClick={() => onDepthClick(depth)}
                              >
                                {depth.name}
                                <div style={{
                                  height: 3,
                                  width: '40%',
                                  backgroundColor: getDepthColor(depth, index),
                                }}
                                />
                              </div>
                            ))}
                          </div>
                        )} */}
                      </div>
                    )
                }
              </Col>
            </Row>
          </Col>
          <Col
            hidden={isMobile && dataView}
            flex={1}
            md={8}
            xs={24}
            className="parameter-detail-others"
            style={{
              borderTopRightRadius: isMobile ? 0 : 10,
              borderBottomRightRadius: isMobile ? 0 : 10,
              overflowX: 'hidden',
              overflowY: 'scroll',
            }}
          >
            <div className="h3" style={{ color: '#fff', marginBottom: 10 }}>
              Discover More Parameters
            </div>
            <div style={{
              display: 'flex', padding: '0px 0px 20px 0px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
            }}
            >
              <ParameterFilters onCategoriesChange={onCategoriesChange} categorySelected={categorySelected} categoriesPresent={categoryArray} />
            </div>
            <FeaturedParameters
              parameters={externalParameterObs}
              onClick={(parameter) => onClick(parameter)}
              // onClickCompare={(parameter) => onClickCompare(parameter)}
              // comparisonParameters={comparisonParameters}
              categorySelected={categorySelected}
              categoriesPresent={categoryArray}
              handleSetCategoryArray={handleSetCategoryArray}
              currentParameter={parameter}
            />
          </Col>
        </Row>
      </div>
      <SocialShareRecipientModal />
      <img src={`${window.location.origin}/images/seagullLogo.png`} id="logo" alt="" style={{ display: 'none' }} />
      <CustomModal
        visible={showShareModal}
        onCancel={() => {
          setShowShareModal(!showShareModal);
          if (!hasUserSeenShareModal && authStatus !== 'authenticated') {
            sessionStorage.setItem('hasUserSeenShareModal', true);
            setShowSignupModal(true);
          }
        }}
        footer={null}
        width="650px"
        styles={{ backgroundColor: 'black', borderRadius: 25 }}
        style={{ top: isMobile ? null : '50%', translate: isMobile ? null : '0 -50%' }}
        className="socialShare"
        isSocialShare
      >
        <SocialShareSenderModal
          shareImageLink={shareImageLink}
          shareImageUrl={shareImageUrl}
          onDownloadPng={onDownloadPng}
        />
      </CustomModal>
      <CustomModal
        visible={showSignupModal}
        onCancel={() => setShowSignupModal(false)}
        footer={null}
        width="605px"
        styles={{ backgroundColor: 'black', borderRadius: 25 }}
        style={{ top: isMobile ? null : '50%', translate: isMobile ? null : '0 -50%' }}
        className="socialShare communitySignup"
        isSocialShare
        isCommunityModal
      >
        <CommunitySignupModal handleCommunityModalSignup={handleCommunityModalSignup} />
      </CustomModal>
      <CustomModal
        visible={showDownloadChartModal}
        onCancel={() => setShowDownloadChartModal(!showDownloadChartModal)}
        footer={null}
        width="690px"
        styles={{ backgroundColor: 'black', borderRadius: 25 }}
        style={isMobile ? { height: '100%', display: 'flex', top: 0 } : ''}
        className="downloadChartModal"
      >
        <DownloadChartModal onDownloadPng={onDownloadPng} onDownloadSvg={onDownloadSvg} />
      </CustomModal>
      <div hidden><canvas id="canvas-holder" ref={canvasRef} /></div>
      {/* Hidden chart for social sharing and high-res download */}
      {chartData[0]?.data?.length > 0 && (
        <>
          <div style={{
            position: 'absolute',
            zIndex: -1,
            visibility: 'hidden',
            top: -9999,
            left: -9999,
          }}
          >
            {!isWindroseChart
            && (
              <CustomChart
                type={chartData[0]?.data?.length > 1 ? 'line' : 'scatter'}
                dotsVisible={chartData[0]?.data?.length > 1 ? dotsVisible : true}
                linesVisible={chartData[0]?.data?.length > 1 ? linesVisible : false}
                series={chartData}
                height={isMobile ? 200 : 250}
                apexChartRef={apexChartRef}
                width={isMobile ? 500 : 650}
                chartId="seagull-share-chart"
                isSocialChart
              />
            )}
            {isWindroseChart
            && (
              <WindroseChart
                series={chartData}
                height={isMobile ? (containerWidth - 10) : (containerHeight - 300)}
                width={containerWidth - 10}
                className="windroseStyles"
                apexChartRef={apexChartRef}
                id="seagull-windrose-chart"
              />
            )}
          </div>
          <div style={{
            position: 'absolute',
            zIndex: -1,
            visibility: 'hidden',
            top: -9999,
            left: -9999,
          }}
          >
            {!isWindroseChart
            && (
              <CustomChart
                type={chartData[0].data.length > 1 ? 'line' : 'scatter'}
                dotsVisible={chartData[0].data.length > 1 ? dotsVisible : true}
                linesVisible={chartData[0].data.length > 1 ? linesVisible : false}
                series={chartData}
                height={800 - 100}
                width={800 * (4 / 3)}
                chartId="seagull-share-chart-high-res"
                isHighResChart
              />
            )}
            {isWindroseChart
            && (
              <WindroseChart
                series={chartData}
                height={800 - 100}
                width={800}
                className="windroseStyles"
                id="seagull-windrose-chart-high-res"
                isHighResChart
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

ExternalParameterDetail.propTypes = {};

ExternalParameterDetail.defaultProps = {};

export default memo(ExternalParameterDetail);
